/**
 * Party Detail — full-page experience.
 *
 * 1:1 with the design folder's `party-details.html`. Replaces what
 * was previously a slide-over preview at `/parties/:partyId`.
 *
 * Layout:
 *   Topbar (party context) — back arrow + avatar + name/type +
 *     phone·email + action buttons (Transaction, Edit)
 *   Tabs: Ledger · Profile Info · Linked Projects · Files
 *
 *   Ledger tab
 *     - 3 KPI cards: Net Balance · Total Received · Total Paid
 *     - 2-col grid (1fr | 360px):
 *       - LEFT: transaction table (search + type filter, shared
 *         `.desktop-txn-table` styling)
 *       - RIGHT: Export statement card (PDF stub)
 *
 *   Profile Info tab
 *     - Identity / Contact / Address / Opening balance / KYC / Bank
 *       sections rendered from the live party doc
 *
 *   Linked Projects tab
 *     - Derived from this party's transactions — one card per unique
 *       projectId, showing project name + per-project net balance +
 *       txn count + click-through to project detail
 *
 *   Files tab
 *     - Placeholder until R2 attachments ship
 *
 * Real-time:
 *   - `useParty(partyId)` for the party doc
 *   - `usePartyTransactions(orgId, partyId)` for the ledger
 *   - `useProjects()` for project name lookup
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PartyFormSlideOver } from '@/components/PartyFormSlideOver';
import { TransactionSlideOver } from '@/components/TransactionSlideOver';
import {
  deleteParty,
  formatIndianPhone,
  maskedAadhar,
  maskedAccount,
  partyTypeColors,
  partyTypeLabel,
  partyTypePillColors,
} from '@/lib/parties';
import {
  categoryLabel,
  paymentMethodLabel,
  type TransactionCategory,
  type PaymentMethod,
} from '@/lib/transactions';
import {
  isCountedInTotals,
  normaliseTxType,
  txDateMs,
  type TransactionItem,
} from '@/lib/useProjectTransactions';
import { usePartyOpenAdvances } from '@/lib/useAdvances';
import { useParty } from '@/lib/useParties';
import { usePartyTransactions } from '@/lib/usePartyTransactions';
import { useProjects } from '@/lib/useProjects';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { canPartyWrite } from '@shared/perms';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
}

function formatInr(n: number): string {
  const abs = Math.abs(n);
  return `₹${abs.toLocaleString('en-IN')}`;
}

function formatSignedInr(n: number): string {
  if (n > 0) return `+${n.toLocaleString('en-IN')}`;
  if (n < 0) return `-${Math.abs(n).toLocaleString('en-IN')}`;
  return '0';
}

function formatDateParts(ms: number): { day: string; year: string } {
  const d = new Date(ms);
  return {
    day: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    year: String(d.getFullYear()),
  };
}

type TabKey = 'ledger' | 'profile' | 'projects' | 'files';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'ledger',   label: 'Ledger' },
  { key: 'profile',  label: 'Profile Info' },
  { key: 'projects', label: 'Linked Projects' },
  { key: 'files',    label: 'Files' },
];

export function PartyDetailPage() {
  const navigate = useNavigate();
  const { partyId } = useParams<{ partyId: string }>();
  const [searchParams] = useSearchParams();
  // Optional project scope — when set, the Ledger tab is filtered to
  // transactions on this project only. Set by the Project Detail →
  // Party tab's row click. Customer can clear with the "View full
  // ledger" link inside the scope banner.
  const projectIdScope = searchParams.get('projectId');

  const { party, loading: partyLoading, error: partyError } = useParty(partyId);
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canPartyWrite(orgRole);
  const orgId = party?.orgId ?? null;
  const { transactions: allTransactions, loading: txnLoading } =
    usePartyTransactions(orgId, partyId);
  const { projects } = useProjects();

  // Apply the project-scope filter (if any) at the top so KPIs +
  // table + Linked Projects tab all see the same dataset.
  const transactions = useMemo(() => {
    if (!projectIdScope) return allTransactions;
    return allTransactions.filter((t) => t.projectId === projectIdScope);
  }, [allTransactions, projectIdScope]);

  const scopedProject = useMemo(
    () =>
      projectIdScope
        ? projects.find((p) => p.id === projectIdScope) ?? null
        : null,
    [projects, projectIdScope],
  );

  const [activeTab, setActiveTab] = useState<TabKey>('ledger');
  const [formOpen, setFormOpen] = useState(false);
  const [previewTxnId, setPreviewTxnId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Strip "+" and spaces to make the phone usable in tel: / wa.me URLs.
  const phoneE164Digits = (party?.phone ?? '').replace(/[^\d]/g, '');
  const hasPhone = phoneE164Digits.length > 0;
  const hasEmail = !!party?.email;

  async function handleDeleteParty() {
    if (!party) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteParty(party.id);
      navigate('/parties');
    } catch (e) {
      setDeleteError((e as Error).message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  // ───── Loading / not-found states ─────
  if (partyLoading) {
    return (
      <div style={{ padding: 64, textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading party…
      </div>
    );
  }
  if (partyError) {
    return (
      <div style={{ padding: 64, textAlign: 'center', color: 'var(--critical)' }}>
        Couldn't load party: {partyError}
      </div>
    );
  }
  if (!party) {
    return (
      <div style={{ padding: 64, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
          <i className="ph ph-address-book" />
        </div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Party not found</div>
        <button
          type="button"
          className="btn-secondary"
          style={{ marginTop: 16 }}
          onClick={() => navigate('/parties')}
        >
          Back to Parties
        </button>
      </div>
    );
  }

  const avatarColors = partyTypeColors(party.partyType);
  const pillColors = partyTypePillColors(party.partyType);

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* ─── Party context header ─── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          padding: '0 0 20px 0',
          marginBottom: 20,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          className="btn-ghost"
          onClick={() =>
            projectIdScope
              ? navigate(`/projects/${projectIdScope}?tab=party`)
              : navigate('/parties')
          }
          style={{
            padding: 8,
            border: '1px solid var(--border)',
            borderRadius: 6,
            cursor: 'pointer',
          }}
          aria-label={projectIdScope ? 'Back to Project' : 'Back to Parties'}
          title={projectIdScope ? 'Back to Project' : 'Back to Parties'}
        >
          <i className="ph ph-arrow-left" style={{ fontSize: 16 }} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: avatarColors.bg,
              color: avatarColors.fg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {initials(party.name)}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                {party.name}
              </h1>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: pillColors.bg,
                  color: pillColors.fg,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                {partyTypeLabel(party.partyType)}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {formatIndianPhone(party.phone)}
              {party.email ? <> &nbsp;·&nbsp; {party.email}</> : null}
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Quick-action buttons (Call / WhatsApp / Email) — only
              when the underlying contact channel exists. tel: + wa.me
              open the native handler (system dialer / WhatsApp
              desktop or web) so this works the same on desktop +
              tablet without us needing a special integration. */}
          {hasPhone ? (
            <>
              <a
                className="btn-secondary"
                href={`tel:+${phoneE164Digits}`}
                title={`Call ${party.name}`}
                style={{
                  height: 32,
                  fontSize: 12,
                  padding: '0 12px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <i className="ph ph-phone" /> Call
              </a>
              <a
                className="btn-secondary"
                href={`https://wa.me/${phoneE164Digits}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`WhatsApp ${party.name}`}
                style={{
                  height: 32,
                  fontSize: 12,
                  padding: '0 12px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  color: '#16A34A',
                }}
              >
                <i className="ph ph-whatsapp-logo" /> WhatsApp
              </a>
            </>
          ) : null}
          {hasEmail ? (
            <a
              className="btn-secondary"
              href={`mailto:${party.email}`}
              title={`Email ${party.name}`}
              style={{
                height: 32,
                fontSize: 12,
                padding: '0 12px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <i className="ph ph-envelope" /> Email
            </a>
          ) : null}

          <span
            style={{
              width: 1,
              height: 20,
              background: 'var(--border)',
              margin: '0 4px',
            }}
          />

          {canWrite && (
            <button
              type="button"
              className="btn-secondary"
              style={{ height: 32, fontSize: 12, padding: '0 12px' }}
              onClick={() => setFormOpen(true)}
            >
              <i className="ph ph-pencil-simple" /> Edit
            </button>
          )}

          {/* Delete — inline confirm to avoid an extra modal layer. */}
          {canWrite && (
            confirmDelete ? (
              <>
                <span style={{ fontSize: 12, color: 'var(--critical)', marginLeft: 6 }}>
                  Delete this party?
                </span>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  style={{ height: 32, fontSize: 12 }}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleDeleteParty}
                  disabled={deleting}
                  style={{
                    height: 32,
                    fontSize: 12,
                    background: 'var(--critical)',
                    borderColor: 'var(--critical)',
                  }}
                >
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn-secondary"
                style={{ height: 32, fontSize: 12, color: 'var(--critical)' }}
                onClick={() => setConfirmDelete(true)}
                title="Delete party"
              >
                <i className="ph ph-trash" />
              </button>
            )
          )}
        </div>
      </div>

      {deleteError ? (
        <div
          style={{
            padding: 12,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 6,
            color: 'var(--critical)',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          Couldn't delete party: {deleteError}
        </div>
      ) : null}

      {/* Project-scope chip — when entering from a project, the
          ledger stays locked to that project's transactions. To see
          the full ledger across projects, customer opens this party
          from the org-wide Parties list instead. */}
      {projectIdScope ? (
        <div
          style={{
            background: 'var(--brand-bg)',
            border: '1px solid var(--brand)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
          }}
        >
          <i
            className="ph-bold ph-funnel"
            style={{ color: 'var(--brand)', fontSize: 16 }}
          />
          <div style={{ flex: 1, color: 'var(--text-primary)' }}>
            Showing entries on{' '}
            <b>{scopedProject?.name ?? 'this project'}</b> only.
          </div>
        </div>
      ) : null}

      {/* ─── Open advances summary ─── (visible only when this
          party is holding company cash — i.e. has ≥1 open or
          partial staff_advance). Surfaces the wallets so an
          admin always knows "who's holding what" without
          drilling into a project. */}
      <PartyOpenAdvancesCard
        orgId={orgId}
        partyId={partyId ?? null}
        projects={projects}
        onOpenProject={(id, advanceId) =>
          navigate(`/projects/${id}?tab=transactions&txn=${advanceId}`)
        }
      />

      {/* ─── Tabs ─── (Linked Projects hidden when scoped — only
          one project to show, so the tab is redundant) */}
      <div className="pd-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 0, marginBottom: 20 }}>
        <div className="pd-tabs">
          {TABS.filter((t) => !(projectIdScope && t.key === 'projects')).map((t) => (
            <button
              key={t.key}
              type="button"
              className={`pd-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pd-content">
        {activeTab === 'ledger' ? (
          <LedgerTab
            transactions={transactions}
            loading={txnLoading}
            projects={projects}
            onOpenTransaction={(id) => setPreviewTxnId(id)}
          />
        ) : activeTab === 'profile' ? (
          <ProfileTab party={party} />
        ) : activeTab === 'projects' ? (
          <LinkedProjectsTab
            transactions={transactions}
            projects={projects}
            onOpenProject={(id) => navigate(`/projects/${id}`)}
          />
        ) : (
          <FilesTab />
        )}
      </div>

      {/* Slide-overs */}
      <PartyFormSlideOver
        open={formOpen}
        onClose={() => setFormOpen(false)}
        orgId={orgId}
        party={party}
        onSaved={() => setFormOpen(false)}
      />
      <TransactionSlideOver
        txnId={previewTxnId}
        onClose={() => setPreviewTxnId(null)}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: LEDGER
// ────────────────────────────────────────────────────────────

function LedgerTab({
  transactions,
  loading,
  projects,
  onOpenTransaction,
}: {
  transactions: TransactionItem[];
  loading: boolean;
  projects: { id: string; name: string }[];
  onOpenTransaction: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'in' | 'out'>('all');

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  // KPIs (posted-only).
  const kpis = useMemo(() => {
    let inSum = 0;
    let outSum = 0;
    for (const t of transactions) {
      if (!isCountedInTotals(t)) continue;
      const k = normaliseTxType(t.type);
      if (k === 'in') inSum += t.amount ?? 0;
      else if (k === 'out') outSum += t.amount ?? 0;
    }
    return { received: inSum, paid: outSum, balance: inSum - outSum };
  }, [transactions]);

  // Filtered + running-balance annotated rows. Running balance is
  // computed chronologically (oldest → newest) then reversed for
  // display.
  const rows = useMemo(() => {
    const chronological = [...transactions].sort(
      (a, b) => txDateMs(a) - txDateMs(b),
    );
    let running = 0;
    const enriched = chronological.map((t) => {
      const k = normaliseTxType(t.type);
      const counted = isCountedInTotals(t);
      if (counted) {
        if (k === 'in') running += t.amount ?? 0;
        else if (k === 'out') running -= t.amount ?? 0;
      }
      return { t, runningBalance: running };
    });
    enriched.reverse();
    const q = search.trim().toLowerCase();
    return enriched.filter(({ t }) => {
      const k = normaliseTxType(t.type);
      if (typeFilter === 'in' && k !== 'in') return false;
      if (typeFilter === 'out' && k !== 'out') return false;
      if (!q) return true;
      const blob = [
        t.description ?? '',
        categoryLabel(t.category as TransactionCategory | undefined),
        paymentMethodLabel(t.paymentMethod as PaymentMethod | undefined),
        t.referenceNumber ?? '',
        projectNameById.get(t.projectId) ?? '',
      ].join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [transactions, search, typeFilter, projectNameById]);

  return (
    <div>
      {/* KPI Strip */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <KpiCard
          label="Net Balance"
          value={formatInr(kpis.balance)}
          accent={
            kpis.balance > 0 ? '#10B981' : kpis.balance < 0 ? '#EF4444' : 'var(--text-primary)'
          }
          subtitle={
            kpis.balance > 0
              ? 'Advance with party'
              : kpis.balance < 0
                ? 'Payable to party'
                : 'Settled'
          }
        />
        <KpiCard label="Total Received" value={formatInr(kpis.received)} />
        <KpiCard label="Total Paid" value={formatInr(kpis.paid)} />
      </div>

      {/* 2-col layout */}
      <div
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 360px',
          gap: 24,
          alignItems: 'start',
        }}
      >
        {/* LEFT — table */}
        <div>
          <div
            className="card"
            style={{
              padding: 0,
              border: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            {/* Filter strip */}
            <div
              style={{
                padding: '12px 16px',
                background: '#fff',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <i
                    className="ph ph-magnifying-glass"
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-tertiary)',
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search transactions…"
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    style={{
                      padding: '8px 12px 8px 36px',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      fontSize: 13,
                      width: 250,
                      background: '#FAFAFA',
                    }}
                  />
                </div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.currentTarget.value as 'all' | 'in' | 'out')}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    fontSize: 13,
                    background: '#FAFAFA',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <option value="all">All Types</option>
                  <option value="in">Payment In</option>
                  <option value="out">Payment Out</option>
                </select>
              </div>

              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
                Loading transactions…
              </div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>
                  <i className="ph ph-currency-inr" />
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                  No transactions yet
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Entries logged against this party will appear here.
                </div>
              </div>
            ) : (
              <table
                className="desktop-txn-table"
                style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}
              >
                <thead>
                  <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                    <Th>Date</Th>
                    <Th>Description</Th>
                    <Th>Project</Th>
                    <Th>Method / Ref</Th>
                    <Th align="right">Amount (₹)</Th>
                    <th style={{ padding: '8px 16px', width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ t, runningBalance }) => {
                    const k = normaliseTxType(t.type);
                    const dateMs = txDateMs(t);
                    const { day, year } = formatDateParts(dateMs);
                    const projectName = projectNameById.get(t.projectId) ?? '—';
                    const isIncome = k === 'in';
                    const iconBg = isIncome ? '#D1FAE5' : '#FEE2E2';
                    const iconFg = isIncome ? '#059669' : '#DC2626';
                    const iconCls = isIncome ? 'ph ph-arrow-down-left' : 'ph ph-arrow-up-right';
                    const amountColor = isIncome ? '#10B981' : 'var(--text-primary)';
                    const signedAmount = isIncome ? (t.amount ?? 0) : -(t.amount ?? 0);
                    const title =
                      t.description?.trim() ||
                      categoryLabel(t.category as TransactionCategory | undefined) ||
                      (isIncome ? 'Payment received' : 'Payment paid');
                    const subtitle = categoryLabel(t.category as TransactionCategory | undefined);
                    return (
                      <tr
                        key={t.id}
                        className="dt-row"
                        onClick={() => onOpenTransaction(t.id)}
                      >
                        <td style={{ padding: '12px 16px', verticalAlign: 'middle', width: 100 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                            {day}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{year}</div>
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: iconBg,
                                color: iconFg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: 16,
                                flexShrink: 0,
                              }}
                            >
                              <i className={iconCls} />
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: 'var(--text-primary)',
                                  marginBottom: 2,
                                }}
                              >
                                {title}
                              </div>
                              {subtitle ? (
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                  {subtitle}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: 'var(--text-secondary)',
                              background: '#F3F4F6',
                              padding: '4px 8px',
                              borderRadius: 6,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {projectName}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                            {paymentMethodLabel(t.paymentMethod as PaymentMethod | undefined)}
                          </div>
                          {t.referenceNumber ? (
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                              {t.referenceNumber}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'middle', textAlign: 'right' }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: amountColor,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {formatSignedInr(signedAmount)}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--text-tertiary)',
                              marginTop: 2,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            Bal: {runningBalance.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'middle', textAlign: 'right' }}>
                          <button
                            className="btn-ghost dt-action"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenTransaction(t.id);
                            }}
                            aria-label="Open transaction"
                          >
                            <i className="ph-bold ph-caret-right" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* RIGHT — sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, marginTop: 0 }}>
              <i className="ph ph-download-simple" /> Export Statement
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Download the full transaction ledger for this party.
            </p>
            <button
              type="button"
              className="btn-secondary"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => alert('PDF export coming soon')}
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      style={{
        padding: '8px 16px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        textAlign: align ?? 'left',
      }}
    >
      {children}
    </th>
  );
}

function KpiCard({
  label,
  value,
  accent,
  subtitle,
}: {
  label: string;
  value: string;
  accent?: string;
  subtitle?: string;
}) {
  return (
    <div className="card" style={{ flex: 1, padding: 20 }}>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          fontWeight: 600,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: accent ?? 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {subtitle ? (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: PROFILE INFO
// ────────────────────────────────────────────────────────────

function ProfileTab({ party }: { party: NonNullable<ReturnType<typeof useParty>['party']> }) {
  const opening = party.openingBalance ?? 0;
  const isReceive = party.openingBalanceType === 'to_receive';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Section title="Contact">
        <Field label="Phone" value={formatIndianPhone(party.phone)} />
        <Field label="Email" value={party.email || '—'} />
        <Field label="Address" value={party.address || '—'} />
        <Field
          label="Joined"
          value={
            party.dateOfJoining?.toMillis?.()
              ? new Date(party.dateOfJoining.toMillis()).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : '—'
          }
        />
      </Section>

      <Section title="KYC">
        <Field label="Aadhar" value={maskedAadhar(party.aadharNumber)} />
        <Field label="PAN" value={party.panNumber || '—'} />
      </Section>

      <Section title="Opening Balance">
        {opening > 0 ? (
          <>
            <Field label="Amount" value={`₹${opening.toLocaleString('en-IN')}`} />
            <Field
              label="Direction"
              value={isReceive ? 'To receive from party' : 'To pay to party'}
            />
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            No opening balance recorded.
          </div>
        )}
      </Section>

      <Section title="Bank Details">
        {party.bankDetails && (party.bankDetails.bankName || party.bankDetails.accountNumber || party.bankDetails.upiId) ? (
          <>
            <Field label="Account Holder" value={party.bankDetails.accountHolderName || '—'} />
            <Field label="Bank" value={party.bankDetails.bankName || '—'} />
            <Field
              label="Account"
              value={maskedAccount(party.bankDetails.accountNumber)}
            />
            <Field label="IFSC" value={party.bankDetails.ifsc || '—'} />
            <Field label="UPI ID" value={party.bankDetails.upiId || '—'} />
            <Field label="Bank Address" value={party.bankDetails.bankAddress || '—'} />
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            No bank details on file.
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <h3
        className="detail-section-title"
        style={{ fontSize: 11, fontWeight: 700, marginTop: 0, marginBottom: 16 }}
      >
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
        {label}
      </div>
      <div className="detail-value" style={{ fontSize: 13, color: 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: LINKED PROJECTS
// ────────────────────────────────────────────────────────────

function LinkedProjectsTab({
  transactions,
  projects,
  onOpenProject,
}: {
  transactions: TransactionItem[];
  projects: { id: string; name: string; status?: string; location?: string; siteAddress?: string }[];
  onOpenProject: (id: string) => void;
}) {
  const projectById = useMemo(() => {
    const m = new Map<string, (typeof projects)[number]>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const rows = useMemo(() => {
    const byProject = new Map<
      string,
      { inSum: number; outSum: number; txnCount: number; lastMs: number }
    >();
    for (const t of transactions) {
      const pid = t.projectId;
      if (!pid) continue;
      const cur =
        byProject.get(pid) ?? { inSum: 0, outSum: 0, txnCount: 0, lastMs: 0 };
      cur.txnCount += 1;
      cur.lastMs = Math.max(cur.lastMs, txDateMs(t));
      if (isCountedInTotals(t)) {
        const k = normaliseTxType(t.type);
        if (k === 'in') cur.inSum += t.amount ?? 0;
        else if (k === 'out') cur.outSum += t.amount ?? 0;
      }
      byProject.set(pid, cur);
    }
    return Array.from(byProject.entries())
      .map(([projectId, agg]) => ({
        projectId,
        project: projectById.get(projectId),
        balance: agg.inSum - agg.outSum,
        txnCount: agg.txnCount,
        lastMs: agg.lastMs,
      }))
      .sort((a, b) => b.lastMs - a.lastMs);
  }, [transactions, projectById]);

  if (rows.length === 0) {
    return (
      <div className="card" style={{ padding: 48, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
          <i className="ph ph-folder-simple" />
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          No linked projects yet
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 360, margin: '0 auto' }}>
          Once you log a transaction with this party against a project, the project will appear here.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
      {rows.map((r) => {
        const variant =
          r.balance > 0 ? 'to-receive' : r.balance < 0 ? 'to-pay' : 'neutral';
        const balanceColor =
          r.balance > 0 ? '#10B981' : r.balance < 0 ? '#EF4444' : 'var(--text-secondary)';
        return (
          <div
            key={r.projectId}
            className="card"
            style={{ padding: 16, cursor: 'pointer' }}
            onClick={() => onOpenProject(r.projectId)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#F3F4F6',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                <i className="ph ph-folder-simple" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.project?.name ?? 'Unknown project'}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.project?.location || r.project?.siteAddress || '—'}
                </div>
              </div>
              <i className="ph-bold ph-caret-right" style={{ color: 'var(--text-tertiary)' }} />
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 12,
                borderTop: '1px solid var(--border)',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {variant === 'to-receive'
                    ? 'To receive'
                    : variant === 'to-pay'
                      ? 'To pay'
                      : 'Settled'}
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: balanceColor,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatInr(r.balance)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Entries
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {r.txnCount}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: FILES (placeholder)
// ────────────────────────────────────────────────────────────

function FilesTab() {
  return (
    <div className="card" style={{ padding: 48, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
        <i className="ph ph-paperclip" />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
        Files coming soon
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 360, margin: '0 auto' }}>
        KYC documents, signed contracts and supporting attachments will live here once uploads ship.
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// OPEN ADVANCES CARD
// ────────────────────────────────────────────────────────────
//
// Renders a "wallet roll-up" of every staff_advance this party
// is currently holding (across every project). Hidden when the
// party has no open advances so the card never adds noise to
// parties who are vendors / clients / etc.
//
// Each row shows: project name + amount given + unsettled
// balance + a "View" chevron that deep-links to the project's
// Transactions tab focused on that advance row.

function PartyOpenAdvancesCard({
  orgId,
  partyId,
  projects,
  onOpenProject,
}: {
  orgId: string | null;
  partyId: string | null;
  projects: import('@/lib/useProjects').ProjectListItem[];
  onOpenProject: (projectId: string, advanceId: string) => void;
}) {
  const { advances } = usePartyOpenAdvances(orgId, partyId);
  if (!advances.length) return null;

  // Total amount this party is currently holding (sum of all
  // open advances). Doesn't account for child bills/refunds —
  // that detail lives one click deep. We surface the gross
  // here as a quick alert ("this person holds ₹X of our cash").
  const grossHeld = advances.reduce((s, a) => s + (a.amount ?? 0), 0);
  const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;

  return (
    <div
      className="card"
      style={{
        padding: 0,
        marginBottom: 16,
        overflow: 'hidden',
        border: '1px solid #FDE68A',
      }}
    >
      <div
        className="card-header"
        style={{
          background: '#FEF3C7',
          borderBottom: '1px solid #FDE68A',
          color: '#92400E',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <i className="ph-bold ph-wallet" />
        <div
          className="card-title"
          style={{ fontSize: 12, color: '#92400E' }}
        >
          OPEN ADVANCES
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#92400E',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          Holding {fmt(grossHeld)} across{' '}
          {advances.length} advance{advances.length === 1 ? '' : 's'}
        </div>
      </div>
      <div>
        {advances.map((a) => {
          const proj = projects.find((p) => p.id === a.projectId);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onOpenProject(a.projectId, a.id)}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: '#fff',
                border: 0,
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <i
                className="ph-bold ph-folder-simple"
                style={{ color: 'var(--text-tertiary)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {proj?.name ?? 'Project'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  Given {fmt(a.amount ?? 0)} ·{' '}
                  {a.advanceStatus === 'partial'
                    ? 'Partial — bills filed'
                    : 'No bills yet'}
                </div>
              </div>
              <i
                className="ph-bold ph-caret-right"
                style={{ color: 'var(--text-tertiary)' }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
